import {
    GovernanceV2Event,
    portalTelemetry,
} from '@experiences/telemetry';

import type {
    PinSource,
    TelemetryAbandonState,
    TelemetryClientMode,
    TelemetryDeployOutcome,
    TelemetryUpgradeOutcome,
} from '../templateDeploy/types';

// Typed telemetry helpers for the Dashboard library — the pre-built template catalog, its deploy
// flow, and the pinning that puts a deployment in the nav. Callers use these named functions rather
// than portalTelemetry.trackEvent directly (same convention as complianceViolationsTelemetry /
// compliancePacksTelemetry). Base dimensions — org / tenant / user / session / app / region /
// timestamp — are attached by the telemetry client, so they are never passed here.
//
// Template ids and package names are product identifiers, not customer data, and are logged as-is.
// Deployment titles are not: a user can rename a deployment to anything.

// ── Discovery ──
export const trackTemplateOpened = (templateId: string) =>
    portalTelemetry.trackEvent({
        name: GovernanceV2Event.DashboardTemplateOpened,
        properties: { TemplateId: templateId },
    });

/** Drawer closed without deploying. With Opened, this is the drop-off at the form. */
export const trackTemplateAbandoned = (templateId: string, lastState: TelemetryAbandonState) =>
    portalTelemetry.trackEvent({
        name: GovernanceV2Event.DashboardTemplateAbandoned,
        properties: {
            TemplateId: templateId,
            LastState: lastState,
        },
    });

// ── Deploy funnel ──
export const trackDeployStarted = (templateId: string, clientMode: TelemetryClientMode) =>
    portalTelemetry.trackEvent({
        name: GovernanceV2Event.DashboardTemplateDeployStarted,
        properties: {
            TemplateId: templateId,
            ClientMode: clientMode,
        },
    });

export const trackDeployCompleted = (args: {
    templateId: string;
    outcome: TelemetryDeployOutcome;
    clientCreated: boolean;
    hasWarnings: boolean;
}) =>
    portalTelemetry.trackEvent({
        name: GovernanceV2Event.DashboardTemplateDeployCompleted,
        properties: {
            TemplateId: args.templateId,
            Outcome: args.outcome,
            ClientCreated: args.clientCreated,
            HasWarnings: args.hasWarnings,
        },
    });

/**
 * `errorCode` is the backend's own code, not a message — the taxonomy has ~20 of them and this is
 * the only signal for which ones actually occur. `isRetryable` says whether a retry was offered.
 */
export const trackDeployFailed = (args: {
    templateId: string;
    errorCode: string;
    isRetryable: boolean;
}) =>
    portalTelemetry.trackEvent({
        name: GovernanceV2Event.DashboardTemplateDeployFailed,
        properties: {
            TemplateId: args.templateId,
            ErrorCode: args.errorCode,
            IsRetryable: args.isRetryable,
        },
    });

// ── Upgrade ──
export const trackUpgradeCompleted = (templateId: string, outcome: TelemetryUpgradeOutcome) =>
    portalTelemetry.trackEvent({
        name: GovernanceV2Event.DashboardUpgradeCompleted,
        properties: {
            TemplateId: templateId,
            Outcome: outcome,
        },
    });

export const trackUpgradeFailed = (templateId: string, errorCode: string) =>
    portalTelemetry.trackEvent({
        name: GovernanceV2Event.DashboardUpgradeFailed,
        properties: {
            TemplateId: templateId,
            ErrorCode: errorCode,
        },
    });

// ── Pinning ──
export const trackPinToggled = (args: {
    source: PinSource;
    pinned: boolean;
    succeeded: boolean;
}) =>
    portalTelemetry.trackEvent({
        name: GovernanceV2Event.DashboardPinToggled,
        properties: {
            Source: args.source,
            Pinned: args.pinned,
            Outcome: args.succeeded ? 'Confirmed' : 'Failed',
        },
    });

// ── Reliability ──
/** The catalog read failing hides the whole templates section. */
export const trackTemplatesLoadFailed = (reason: string) =>
    portalTelemetry.trackEvent({
        name: GovernanceV2Event.DashboardTemplatesLoadFailed,
        properties: { Reason: reason },
    });
